/* ============================================================
   Starter Kit Funnel — StarterKitFunnel class
   ============================================================ */

class StarterKitFunnel {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.data = window.starterKitFunnelData;
    if (!this.data) return;

    this._normalizePlans();

    this.state = {
      currentStep: 1,
      step1: null,
      step2Selected: [],
      purchaseType: 'subscription',
      step3ItemPlans: {}
    };

    this._lastLearnBtn = null;
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._init();
  }

  _init() {
    this._bindStep1();
    this._bindStep2();
    this._bindStep3Toggle();
    this._bindNavigation();
    this._bindPopup();

    // Animate the progress bar of the step that's visible on load (Step 1).
    const activeStep = this.section.querySelector('.skf__step--active');
    if (activeStep) this._animateProgress(activeStep);
  }

  // Replay the progress fill from 0 so it visibly fills when a step appears,
  // instead of rendering pre-filled (a display:none step never paints width:0).
  _animateProgress(stepEl) {
    const fill = stepEl.querySelector('.skf__progress-fill');
    if (!fill) return;
    fill.style.width = '0';
    void fill.offsetWidth; // force reflow so the 0 width is committed before the transition
    requestAnimationFrame(() => { fill.style.width = ''; });
  }

  /* ============================================================
     Accessibility — live region announcement
     ============================================================ */

  _announce(msg) {
    const el = document.getElementById('skf-live-region');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => { el.textContent = msg; });
  }

  /* ============================================================
     Step 1
     ============================================================ */

  _bindStep1() {
    this.section.querySelectorAll('[data-step="1"] .skf__product-row').forEach(row => {
      // Out-of-stock variants are display-only: not selectable, not focusable.
      if (row.dataset.available === 'false') return;
      row.addEventListener('click', e => {
        // The row is a <label>, so clicking it also fires a synthetic click on the
        // inner radio that bubbles back here. Ignore that one so the toggle fires once.
        if (e.target.matches('input[type="radio"]')) return;
        this._selectStep1(row.dataset.productId);
      });
      row.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._selectStep1(row.dataset.productId);
        }
      });
    });

    const cta = this.section.querySelector('[data-cta-step="1"]');
    if (cta) cta.addEventListener('click', () => {
      if (this.state.step1) this._goToStep(2);
    });
  }

  _selectStep1(productId) {
    const product = this.data.step1Products.find(p => String(p.id) === String(productId));
    if (!product) return;
    // Guard against selecting an out-of-stock variant (data attribute is the source of truth).
    if (product.available === false) return;

    // Clicking the already-selected device deselects it (toggle).
    const isDeselect = this.state.step1 && String(this.state.step1.id) === String(productId);
    this.state.step1 = isDeselect ? null : product;

    this.section.querySelectorAll('[data-step="1"] .skf__product-row').forEach(row => {
      const selected = !isDeselect && String(row.dataset.productId) === String(productId);
      row.classList.toggle('skf__product-row--selected', selected);
      row.setAttribute('aria-checked', selected ? 'true' : 'false');
      const radio = row.querySelector('input[type="radio"]');
      if (radio) radio.checked = selected;
    });

    this.state.step1 ? this._enableCta(1) : this._disableCta(1);
  }

  /* ============================================================
     Step 2
     ============================================================ */

  _bindStep2() {
    this.section.querySelectorAll('[data-step="2"] .skf__product-card').forEach(card => {
      card.addEventListener('click', e => {
        if (e.target.closest('.skf__product-card-learn')) return;
        this._toggleStep2(card.dataset.productId);
      });
      card.addEventListener('keydown', e => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.skf__product-card-learn')) {
          e.preventDefault();
          this._toggleStep2(card.dataset.productId);
        }
      });
    });

    const cta = this.section.querySelector('[data-cta-step="2"]');
    if (cta) cta.addEventListener('click', () => {
      if (this.state.step2Selected.length >= this._step2Min()) this._goToStep(3);
    });

    this._updateMinNotice();
  }

  _step2Min() {
    return Math.max(1, this.data.step2MinSelections || 1);
  }

  // Tells the customer how many more products they must pick before they can proceed.
  _updateMinNotice() {
    const el = this.section.querySelector('[data-min-notice]');
    if (!el) return;
    const remaining = this._step2Min() - this.state.step2Selected.length;
    if (remaining > 0) {
      el.textContent = `Select ${remaining} more ${remaining === 1 ? 'product' : 'products'} to continue`;
      el.classList.add('skf__min-notice--visible');
    } else {
      el.textContent = '';
      el.classList.remove('skf__min-notice--visible');
    }
  }

  _toggleStep2(productId) {
    const product = this.data.step2Products.find(p => String(p.id) === String(productId));
    if (!product) return;

    const max = this.data.step2MaxSelections;
    const idx = this.state.step2Selected.findIndex(p => String(p.id) === String(productId));

    if (idx >= 0) {
      this.state.step2Selected.splice(idx, 1);
    } else {
      if (max > 0 && this.state.step2Selected.length >= max) {
        const notice = this.section.querySelector('.skf__max-notice');
        if (notice) notice.classList.add('skf__max-notice--visible');
        return;
      }
      this.state.step2Selected.push(product);
    }

    const notice = this.section.querySelector('.skf__max-notice');
    if (notice) notice.classList.remove('skf__max-notice--visible');

    const isNowSelected = idx < 0;
    const card = this.section.querySelector(`[data-step="2"] .skf__product-card[data-product-id="${productId}"]`);
    if (card) {
      card.classList.toggle('skf__product-card--selected', isNowSelected);
      card.setAttribute('aria-checked', isNowSelected ? 'true' : 'false');
    }

    this._updateStep2Panel();
    const ready = this.state.step2Selected.length >= this._step2Min();
    ready ? this._enableCta(2) : this._disableCta(2);
    this._updateMinNotice();
  }

  _updateStep2Panel() {
    const img = this.section.querySelector('[data-step="2"] .skf__panel-image--dynamic');
    if (!img) return;
    const src = this.state.step2Selected.length > 0
      ? this.state.step2Selected[0].image
      : (img.dataset.defaultSrc || '');
    if (img.src !== src) img.src = src;
  }

  /* ============================================================
     Step 3 — Build + Toggle
     ============================================================ */

  _bindStep3Toggle() {
    this.section.querySelectorAll('.skf__sub-option').forEach(option => {
      option.addEventListener('click', () => this._setPurchaseType(option.dataset.purchaseType));
      option.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._setPurchaseType(option.dataset.purchaseType);
        }
      });
    });

    const btn = this.section.querySelector('#skf-checkout-btn');
    if (btn) btn.addEventListener('click', () => this._checkout());
  }

  _setPurchaseType(type) {
    this.state.purchaseType = type;

    this.section.querySelectorAll('.skf__sub-option').forEach(el => {
      const active = el.dataset.purchaseType === type;
      el.classList.toggle('skf__sub-option--active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const itemsEl = this.section.querySelector('.skf__order-items');
    if (itemsEl) itemsEl.dataset.purchaseType = type;

    this._updateStep3Prices();
  }

  _buildStep3() {
    this.state.step3ItemPlans = {};
    const itemsEl = this.section.querySelector('#skf-order-items');
    if (!itemsEl) return;
    itemsEl.innerHTML = '';
    itemsEl.dataset.purchaseType = this.state.purchaseType;

    if (this.state.step1) itemsEl.appendChild(this._renderOrderItem(this.state.step1));
    this.state.step2Selected.forEach(p => itemsEl.appendChild(this._renderOrderItem(p)));

    // Sync aria-pressed state on subscription options
    this.section.querySelectorAll('.skf__sub-option').forEach(el => {
      el.setAttribute('aria-pressed', el.dataset.purchaseType === this.state.purchaseType ? 'true' : 'false');
    });

    this._updateStep3Prices(); // hardcoded bundle-discount math — shows instantly
  }

  _renderOrderItem(product) {
    const hasPlans = product.sellingPlans && product.sellingPlans.length > 0;
    const div = document.createElement('div');
    div.className = 'skf__order-item';
    div.dataset.productId = product.id;
    div.dataset.hasPlans = hasPlans ? 'true' : 'false';

    // Default selected plan = the 2-month plan (falls back to the first available). Line price tracks it.
    const defaultPlan = this._defaultPlanFor(product);
    // Final price + "was" are filled by _renderLinePrices() from the live cart, so
    // automatic bundle discounts (e.g. SR BUNDLE) show here — with an instant estimate first.
    const pricesHtml =
      `<span class="skf__price-final" data-line-final></span>
       <s class="skf__price-was" data-line-was aria-label="Original price"></s>`;

    const planSelectId = `skf-plan-${product.id}`;
    const plansHtml = hasPlans
      ? `<div class="skf__ships-every">
           <label class="skf__ships-label" for="${planSelectId}">Ships every</label>
           <select class="skf__plan-select" id="${planSelectId}" data-product-id="${product.id}"
                   aria-label="Delivery frequency for ${product.title}">
             ${product.sellingPlans.map(plan =>
               `<option value="${plan.id}" data-price="${plan.price}"${defaultPlan && plan.id === defaultPlan.id ? ' selected' : ''}>${this._freqText(plan.deliveryLabel)}</option>`
             ).join('')}
           </select>
         </div>`
      : '';

    const variantSuffix = product.variantTitle && product.variantTitle !== 'Default Title'
      ? ` <span class="skf__order-item-variant">${product.variantTitle}</span>`
      : '';

    div.innerHTML = `
      <img class="skf__order-item-img" src="${product.image}" alt="" role="presentation" width="76" height="76">
      <div class="skf__order-item-body">
        <div class="skf__order-item-title">${product.title}${variantSuffix}</div>
        <div class="skf__order-item-prices" aria-label="Price for ${product.title}">${pricesHtml}</div>
        ${plansHtml}
        <div class="skf__one-time-label">One-time purchase</div>
      </div>
    `;

    const select = div.querySelector('.skf__plan-select');
    if (select && defaultPlan) {
      this.state.step3ItemPlans[product.id] = { planId: defaultPlan.id, planPrice: defaultPlan.price };

      select.addEventListener('change', e => {
        const opt = e.target.selectedOptions[0];
        const planPrice = Number(opt.dataset.price);
        this.state.step3ItemPlans[product.id] = { planId: e.target.value, planPrice };

        this._updateStep3Prices();
      });
    }

    return div;
  }

  // Normalize an OrderGroove frequency label so "Ships every {x}" never reads redundantly.
  // "2 months" → "2 months", "Every 6 weeks" → "6 weeks", "Deliver every 2 months" → "2 months"
  _freqText(raw) {
    return (raw || '').replace(/^(ships?\s+every|delivers?\s+every|every)\s+/i, '').trim();
  }

  // Restrict subscription frequencies to the 1-month and 2-month plans only,
  // with the 2-month plan as the default (sorted to come first / preselected).
  // Returns the interval in months for a plan, or null if it isn't a 1- or 2-month plan.
  _planMonths(plan) {
    const s = ((plan.deliveryLabel || '') + ' ' + (plan.name || '')).toLowerCase();
    if (/\b2\s*months?\b/.test(s)) return 2;
    if (/\b1\s*month\b/.test(s) || /\bmonthly\b/.test(s) || /every\s+month\b/.test(s)) return 1;
    return null;
  }

  _normalizePlans() {
    const lists = [this.data.step1Products, this.data.step2Products];
    lists.forEach(list => {
      (list || []).forEach(p => {
        if (!Array.isArray(p.sellingPlans)) return;
        const filtered = p.sellingPlans
          .filter(pl => this._planMonths(pl) !== null)
          .sort((a, b) => this._planMonths(a) - this._planMonths(b)); // 1 month, then 2 months
        p.sellingPlans = filtered;
        p.hasSubscription = filtered.length > 0;
        p.subscriptionPrice = filtered.length
          ? Math.min(...filtered.map(pl => pl.price))
          : null;
      });
    });
  }

  // The plan preselected by default = 2-month (falls back to the first available).
  _defaultPlanFor(product) {
    if (!product.sellingPlans || !product.sellingPlans.length) return null;
    return product.sellingPlans.find(pl => this._planMonths(pl) === 2) || product.sellingPlans[0];
  }

  _updateStep3Prices() {
    const subTotal = this._totalFor('subscription');
    const oneTimeTotal = this._totalFor('one_time');
    const regularSum = this._regularSum();

    const subPriceEl = this.section.querySelector('[data-sub-price]');
    const oneTimePriceEl = this.section.querySelector('[data-one-time-price]');
    if (subPriceEl) subPriceEl.textContent = this._money(subTotal);
    if (oneTimePriceEl) oneTimePriceEl.textContent = this._money(oneTimeTotal);

    // Compare-at on the Subscribe & Save card: the would-be price with no discount,
    // struck through, so the saving is visible. Hidden when there is no saving.
    const subCompareEl = this.section.querySelector('[data-sub-compare]');
    if (subCompareEl) {
      subCompareEl.textContent = regularSum > subTotal ? this._money(regularSum) : '';
    }

    const activeTotal = this.state.purchaseType === 'subscription' ? subTotal : oneTimeTotal;
    const compareTotal = regularSum > activeTotal ? regularSum : null;

    const totalActiveEl = this.section.querySelector('[data-total-active]');
    const totalCompareEl = this.section.querySelector('[data-total-compare]');
    if (totalActiveEl) totalActiveEl.textContent = this._money(activeTotal);
    if (totalCompareEl) totalCompareEl.textContent = compareTotal ? this._money(compareTotal) : '';

    this._renderLinePrices();
  }

  _selectedProducts() {
    return [this.state.step1, ...this.state.step2Selected].filter(Boolean);
  }

  _productById(id) {
    if (this.state.step1 && String(this.state.step1.id) === String(id)) return this.state.step1;
    return this.state.step2Selected.find(p => String(p.id) === String(id));
  }

  // Estimated unit price for one product under a purchase type (before automatic discounts).
  _estimatedUnit(product, type) {
    if (type === 'subscription' && product.sellingPlans && product.sellingPlans.length) {
      const ps = this.state.step3ItemPlans[product.id];
      return ps ? ps.planPrice : (product.subscriptionPrice ?? product.regularPrice);
    }
    return product.regularPrice;
  }

  // Flat bundle discount (in cents) applied to the device line on the subscription
  // path — mirrors the store's "SR BUNDLE" automatic discount. Configured in the
  // Theme Editor (Step 3 Settings → "Bundle discount amount") so prices show instantly
  // without reading the live cart. The real discount still applies at checkout.
  _bundleDiscountFor(type) {
    const amt = this.data.bundleDiscount || 0;
    if (amt <= 0) return 0;
    if (type !== 'subscription') return 0;          // only the subscription path is discounted
    if (!this.state.step1) return 0;                // no device → nothing to discount
    if (this.state.step2Selected.length < this._step2Min()) return 0; // bundle minimum not met
    return amt;
  }

  // The device the bundle discount lands on = the Step 1 selection.
  _isBundleDevice(product) {
    return this.state.step1 && String(product.id) === String(this.state.step1.id);
  }

  // Order total for a purchase type: per-line estimates minus the bundle discount.
  _totalFor(type) {
    const sum = this._selectedProducts().reduce((t, p) => t + this._estimatedUnit(p, type), 0);
    return Math.max(0, sum - this._bundleDiscountFor(type));
  }

  _regularSum() {
    return this._selectedProducts().reduce((s, p) => s + p.regularPrice, 0);
  }

  // What to display on a single order line, with the bundle discount applied to the device.
  _lineDisplay(product) {
    const type = this.state.purchaseType;
    let final = this._estimatedUnit(product, type);
    if (this._isBundleDevice(product)) {
      final = Math.max(0, final - this._bundleDiscountFor(type));
    }
    return { final, was: product.regularPrice };
  }

  _renderLinePrices() {
    this.section.querySelectorAll('.skf__order-item').forEach(div => {
      const product = this._productById(div.dataset.productId);
      if (!product) return;
      const finalEl = div.querySelector('[data-line-final]');
      const wasEl = div.querySelector('[data-line-was]');
      const { final, was } = this._lineDisplay(product);
      if (finalEl) finalEl.textContent = this._money(final);
      if (wasEl) wasEl.textContent = (was != null && was > final) ? this._money(was) : '';
    });
  }

  /* ============================================================
     Navigation
     ============================================================ */

  _bindNavigation() {
    this.section.querySelectorAll('[data-back-step]').forEach(btn => {
      btn.addEventListener('click', () => this._goToStep(Number(btn.dataset.backStep)));
    });
  }

  _goToStep(step) {
    const prev = this.section.querySelector(`[data-step="${this.state.currentStep}"]`);
    const next = this.section.querySelector(`[data-step="${step}"]`);
    if (!next) return;

    if (prev) {
      prev.classList.remove('skf__step--active');
      prev.style.display = 'none';
    }

    next.style.display = '';
    requestAnimationFrame(() => {
      next.classList.add('skf__step--active');
      this._animateProgress(next);
    });

    this.state.currentStep = step;

    if (this._reducedMotion) {
      window.scrollTo(0, 0);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Move focus to the step heading
    requestAnimationFrame(() => {
      const heading = next.querySelector('.skf__step-label, .skf__brand, h1, h2');
      if (heading) {
        if (!heading.getAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    });

    const labels = ['Step 1: Choose Your Device', 'Step 2: Choose Your Products', 'Step 3: Review Your Order'];
    this._announce(labels[step - 1] || `Step ${step}`);

    if (step === 3) this._buildStep3();
  }

  /* ============================================================
     Popup
     ============================================================ */

  _bindPopup() {
    this.section.querySelectorAll('.skf__product-card-learn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this._lastLearnBtn = btn;
        this._openPopup(btn.dataset.productId);
      });
    });

    const overlay = this.section.querySelector('.skf__popup-overlay');
    if (overlay) {
      overlay.addEventListener('click', e => { if (e.target === overlay) this._closePopup(); });
    }

    const closeBtn = this.section.querySelector('.skf__popup-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this._closePopup());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this._closePopup();
      if (e.key === 'Tab') this._trapPopupFocus(e);
    });
  }

  _trapPopupFocus(e) {
    const overlay = this.section.querySelector('.skf__popup-overlay--active');
    if (!overlay) return;
    const modal = overlay.querySelector('.skf__popup-modal');
    const focusable = Array.from(
      modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.disabled && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    }
  }

  _openPopup(productId) {
    const product = this.data.step2Products.find(p => String(p.id) === String(productId));
    if (!product || !product.popup) return;

    const bodyEl = this.section.querySelector('#skf-popup-body');
    if (!bodyEl) return;

    const isSelected = this.state.step2Selected.some(p => String(p.id) === String(productId));
    bodyEl.innerHTML = this._renderPopupContent(product, isSelected);
    this._bindPopupActions(bodyEl, productId);

    const overlay = this.section.querySelector('.skf__popup-overlay');
    if (overlay) {
      overlay.classList.add('skf__popup-overlay--active');
      overlay.setAttribute('aria-labelledby', 'skf-popup-title');
      // Focus close button
      requestAnimationFrame(() => {
        const close = overlay.querySelector('.skf__popup-close');
        if (close) close.focus();
      });
    }
    document.body.classList.add('skf-no-scroll');
  }

  _closePopup() {
    const overlay = this.section.querySelector('.skf__popup-overlay');
    if (!overlay || !overlay.classList.contains('skf__popup-overlay--active')) return;
    overlay.classList.remove('skf__popup-overlay--active');
    document.body.classList.remove('skf-no-scroll');
    if (this._lastLearnBtn) {
      this._lastLearnBtn.focus();
      this._lastLearnBtn = null;
    }
  }

  _bindPopupActions(bodyEl, productId) {
    bodyEl.querySelectorAll('.skf__popup-faq-question').forEach(q => {
      q.addEventListener('click', () => {
        const item = q.closest('.skf__popup-faq-item');
        const open = item.classList.toggle('skf__popup-faq-item--open');
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    const selectBtn = bodyEl.querySelector('.skf__popup-select-btn');
    if (selectBtn) {
      selectBtn.addEventListener('click', () => {
        this._toggleStep2(productId);
        this._closePopup();
      });
    }
  }

  _renderPopupContent(product, isSelected) {
    const p = product.popup;
    const selectLabel = isSelected ? 'Remove from Kit' : (p.selectButtonLabel || 'SELECT THIS PRODUCT →');
    const starHtml = p.ratingScore ? this._renderStars(parseFloat(p.ratingScore)) : '';

    const featuresHtml = p.featuresTags && p.featuresTags.length
      ? `<div class="skf__popup-section">
           <div class="skf__popup-section-label" id="skf-popup-features">Features</div>
           <ul class="skf__popup-tags" aria-labelledby="skf-popup-features">
             ${p.featuresTags.map(t => `<li class="skf__popup-tag">${t}</li>`).join('')}
           </ul>
         </div>`
      : '';

    const specsHtml = (p.specsTop || p.specsMiddle || p.specsBottom)
      ? `<div class="skf__popup-section">
           <div class="skf__popup-section-label">Notes</div>
           ${p.specsTop ? `<div class="skf__popup-spec"><span class="skf__popup-spec-label">Top:</span> ${p.specsTop}</div>` : ''}
           ${p.specsMiddle ? `<div class="skf__popup-spec"><span class="skf__popup-spec-label">Middle:</span> ${p.specsMiddle}</div>` : ''}
           ${p.specsBottom ? `<div class="skf__popup-spec"><span class="skf__popup-spec-label">Bottom:</span> ${p.specsBottom}</div>` : ''}
         </div>`
      : '';

    const badgesHtml = p.badges && p.badges.length
      ? `<ul class="skf__popup-badges" aria-label="Product certifications">
           ${p.badges.map(b => `<li class="skf__popup-badge-item">
             <span class="skf__popup-badge-icon" aria-hidden="true">✓</span>${b}
           </li>`).join('')}
         </ul>`
      : '';

    const aboutHtml = (p.aboutImage || p.aboutSubtitle || p.aboutDescription)
      ? `<div class="skf__popup-about">
           <div class="skf__popup-section-label">About</div>
           ${p.aboutImage ? `<img class="skf__popup-about-img" src="${p.aboutImage}" alt="" loading="lazy">` : ''}
           ${p.aboutSubtitle ? `<p class="skf__popup-about-subtitle">${p.aboutSubtitle}</p>` : ''}
           ${p.aboutDescription ? `<div class="skf__popup-about-desc">${p.aboutDescription}</div>` : ''}
         </div>`
      : '';

    const reviewsHtml = p.reviews && p.reviews.some(r => r.quote)
      ? `<section class="skf__popup-reviews" aria-label="Customer reviews">
           ${p.reviews.filter(r => r.quote).map(r => `
             <div class="skf__popup-review">
               <div class="skf__popup-review-stars" aria-hidden="true">★★★★★</div>
               <blockquote class="skf__popup-review-quote">"${r.quote}"</blockquote>
               <div class="skf__popup-review-meta">
                 <cite class="skf__popup-review-author">— ${r.author || ''}</cite>
                 ${r.verified ? '<span class="skf__popup-verified">VERIFIED</span>' : ''}
               </div>
             </div>`).join('')}
         </section>`
      : '';

    const faqsHtml = p.faqs && p.faqs.some(f => f.question)
      ? `<div class="skf__popup-faqs">
           <div class="skf__popup-section-label">Frequently Asked Questions</div>
           ${p.faqs.filter(f => f.question).map((f, i) => `
             <div class="skf__popup-faq-item">
               <button class="skf__popup-faq-question" type="button"
                       aria-expanded="false" aria-controls="skf-faq-${i}">
                 <span>${f.question}</span>
                 <span class="skf__popup-faq-icon" aria-hidden="true">+</span>
               </button>
               <div class="skf__popup-faq-answer" id="skf-faq-${i}">${f.answer || ''}</div>
             </div>`).join('')}
         </div>`
      : '';

    return `
      <div class="skf__popup-hero">
        <img src="${product.image}" alt="${product.title}" width="520" height="260" loading="eager">
        ${p.badgeLabel ? `<span class="skf__popup-hero-badge" aria-hidden="true">${p.badgeLabel}</span>` : ''}
      </div>
      <div class="skf__popup-content-inner">
        <h2 class="skf__popup-product-name" id="skf-popup-title">${product.title}</h2>
        ${p.ratingScore ? `
          <div class="skf__popup-rating"
               aria-label="Rated ${p.ratingScore} out of 5${p.reviewsCount ? `, ${p.reviewsCount} reviews` : ''}">
            <span class="skf__popup-stars" aria-hidden="true">${starHtml}</span>
            <span class="skf__popup-reviews-text">${p.ratingScore}${p.reviewsCount ? ` — ${p.reviewsCount} reviews` : ''}</span>
          </div>` : ''}
        ${featuresHtml}${specsHtml}
        ${p.description ? `<div class="skf__popup-description">${p.description}</div>` : ''}
        ${badgesHtml}${aboutHtml}${reviewsHtml}${faqsHtml}
      </div>
      <div class="skf__popup-footer">
        <button class="skf__popup-select-btn${isSelected ? ' skf__popup-select-btn--deselect' : ''}" type="button">
          ${selectLabel}
        </button>
      </div>
    `;
  }

  _renderStars(score) {
    return Array.from({ length: 5 }, (_, i) => {
      const n = i + 1;
      return score >= n ? '★' : score >= n - 0.5 ? '½' : '☆';
    }).join('');
  }

  /* ============================================================
     Checkout — suppresses cart drawer via capture-phase interception
     ============================================================ */

  _checkout() {
    const items = this._buildCheckoutItems();
    if (!items.length) return;

    const btn = this.section.querySelector('#skf-checkout-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Processing…'; }

    // Use a pristine fetch so cart-app fetch wrappers (Rebuy Smart Cart, OrderGroove)
    // never see our /cart mutations and no slide-cart auto-opens before we redirect.
    const pf = this._makePristineFetch();
    const cartFetch = pf.fetch;
    const root = window.location.origin + ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');

    cartFetch(root + 'cart/clear.js', { method: 'POST', credentials: 'same-origin' })
      .then(() => cartFetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      }))
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        pf.dispose();
        if (ok) {
          window.location.href = '/checkout';
        } else {
          this._showError(data.description || data.message || 'Something went wrong. Please try again.');
          if (btn) { btn.disabled = false; btn.textContent = 'CHECKOUT →'; }
        }
      })
      .catch(() => {
        pf.dispose();
        this._showError('Something went wrong. Please try again.');
        if (btn) { btn.disabled = false; btn.textContent = 'CHECKOUT →'; }
      });
  }

  // A fetch that bypasses any window.fetch wrapper installed by cart apps.
  // A fresh same-origin iframe carries its own un-wrapped native fetch.
  // Returns { fetch, dispose } so each caller owns and cleans up its own frame.
  _makePristineFetch() {
    try {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.tabIndex = -1;
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      document.body.appendChild(iframe);
      const frameFetch = iframe.contentWindow && iframe.contentWindow.fetch;
      if (frameFetch) return { fetch: frameFetch.bind(iframe.contentWindow), dispose: () => iframe.remove() };
      iframe.remove();
    } catch (e) { /* CSP or sandbox blocked it — fall back below */ }
    return { fetch: window.fetch.bind(window), dispose: () => {} };
  }

  _buildCheckoutItems() {
    return this._itemsForType(this.state.purchaseType);
  }

  // Cart line items for a purchase type. Subscription items carry their selected
  // selling_plan; everything else is added one-time (so automatic bundle discounts apply).
  _itemsForType(type) {
    return this._selectedProducts().map(p => {
      const item = { id: p.variantId, quantity: 1 };
      if (type === 'subscription' && p.sellingPlans && p.sellingPlans.length) {
        const chosen = this.state.step3ItemPlans[p.id];
        const planId = chosen ? chosen.planId : (this._defaultPlanFor(p) || {}).id;
        if (planId) item.selling_plan = Number(planId);
      }
      return item;
    });
  }

  _showError(msg) {
    let el = this.section.querySelector('.skf__checkout-error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'skf__checkout-error';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      const btn = this.section.querySelector('#skf-checkout-btn');
      if (btn) btn.insertAdjacentElement('afterend', el);
    }
    el.textContent = msg;
  }

  /* ============================================================
     Helpers
     ============================================================ */

  _enableCta(step) {
    const btn = this.section.querySelector(`[data-cta-step="${step}"]`);
    if (!btn) return;
    btn.classList.remove('skf__cta--disabled');
    btn.removeAttribute('aria-disabled');
  }

  _disableCta(step) {
    const btn = this.section.querySelector(`[data-cta-step="${step}"]`);
    if (!btn) return;
    btn.classList.add('skf__cta--disabled');
    btn.setAttribute('aria-disabled', 'true');
  }

  // Uses Shopify's formatMoney when available, respecting store currency settings
  _money(cents) {
    if (cents == null) return '';
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('[data-section-type="starter-kit-funnel"]');
  if (section) new StarterKitFunnel(section);
});
